import React, { useCallback, useEffect, useMemo, useState } from "react";
import AtriumApi, { getApiErrorMessage } from "api";
import {
  MdVisibility,
  MdTouchApp,
  MdPeople,
  MdChurch,
  MdAttachMoney,
  MdRefresh,
  MdTrendingUp,
} from "react-icons/md";
import { PageContainer, PageHeader } from "components/page";
import Card, { CardHeader } from "components/card";
import Button from "components/button";
import { SegmentedControl } from "components/tabs";
import StatCard from "components/stat-card";
import Chart from "components/charts/Chart";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { formatNumber } from "utils/format";

const RANGE_OPTIONS = [
  { value: "7", label: "7 Days" },
  { value: "30", label: "30 Days" },
  { value: "90", label: "90 Days" },
];

const TOP_LIST_SIZE = 5;

const formatPercent = (value) => `${formatNumber(value || 0)}%`;

const getDateRange = (days) => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - parseInt(days, 10));
  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0],
  };
};

const ListSkeleton = () => (
  <div className="flex flex-col gap-3">
    {Array.from({ length: TOP_LIST_SIZE }, (_, i) => (
      <Skeleton key={i} className="h-10 w-full rounded-control" />
    ))}
  </div>
);

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dateRange, setDateRange] = useState("30");
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [linkPerformance, setLinkPerformance] = useState([]);
  const [referrers, setReferrers] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { startDate, endDate } = getDateRange(dateRange);

      const [summaryRes, timeseriesRes, linksRes, referrersRes] = await Promise.all([
        AtriumApi.get(`/connect/analytics/summary?startDate=${startDate}&endDate=${endDate}`),
        AtriumApi.get(`/connect/analytics/timeseries?startDate=${startDate}&endDate=${endDate}&metric=page_view`),
        AtriumApi.get(`/connect/analytics/links?startDate=${startDate}&endDate=${endDate}`),
        AtriumApi.get(`/connect/analytics/referrers?startDate=${startDate}&endDate=${endDate}`),
      ]);

      if (summaryRes.data.success) {
        setSummary(summaryRes.data.data);
      }
      if (timeseriesRes.data.success) {
        setTimeseries(timeseriesRes.data.data || []);
      }
      if (linksRes.data.success) {
        setLinkPerformance(linksRes.data.data || []);
      }
      if (referrersRes.data.success) {
        setReferrers(referrersRes.data.data || []);
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const pageViewsChartOptions = useMemo(
    () => ({
      xaxis: {
        type: "datetime",
        categories: timeseries.map((d) => d.date),
        labels: { datetimeUTC: true },
      },
      tooltip: { x: { format: "MMM dd, yyyy" } },
    }),
    [timeseries]
  );

  const pageViewsChartSeries = useMemo(
    () => [{ name: "Page Views", data: timeseries.map((d) => d.count) }],
    [timeseries]
  );

  const funnelData = useMemo(() => {
    if (!summary) return [];
    return [
      { label: "Page Views", value: summary.pageViews || 0 },
      {
        label: "Form Opens",
        value: summary.visitorFormOpens + summary.prayerFormOpens + summary.donationFormOpens || 0,
      },
      {
        label: "Form Submissions",
        value: summary.visitorFormSubmits + summary.prayerFormSubmits + summary.donationCompletes || 0,
      },
    ];
  }, [summary]);

  const funnelMax = Math.max(0, ...funnelData.map((d) => d.value)) || 1;
  const rates = summary?.conversionRates;
  const statsLoading = loading || !summary;

  const header = (
    <PageHeader
      title="Analytics"
      description="Track your Connect page performance"
      actions={
        <>
          <SegmentedControl
            aria-label="Date range"
            value={dateRange}
            onChange={setDateRange}
            options={RANGE_OPTIONS}
          />
          <Button
            variant="secondary"
            leftIcon={<MdRefresh />}
            onClick={fetchAnalytics}
            loading={loading}
          >
            Refresh
          </Button>
        </>
      }
    />
  );

  if (loadError) {
    return (
      <PageContainer>
        {header}
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(loadError, "Could not load analytics")}
            onRetry={fetchAnalytics}
          />
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {header}

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          icon={MdVisibility}
          label="Page Views"
          value={formatNumber(summary?.pageViews || 0)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdTouchApp}
          accent="sky"
          label="Link Clicks"
          value={formatNumber(summary?.linkClicks || 0)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdPeople}
          accent="sky"
          label="Visitors"
          value={formatNumber(summary?.visitorFormSubmits || 0)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdChurch}
          accent="slate"
          label="Prayers"
          value={formatNumber(summary?.prayerFormSubmits || 0)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdAttachMoney}
          accent="gold"
          label="Donations"
          value={formatNumber(summary?.donationCompletes || 0)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdTrendingUp}
          label="Visitor Conversion"
          value={formatPercent(rates?.visitor)}
          loading={statsLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card padding="md" className="gap-4">
          <CardHeader title="Page Views Over Time" />
          <Chart
            type="area"
            series={pageViewsChartSeries}
            options={pageViewsChartOptions}
            loading={loading}
            empty={timeseries.length === 0}
            emptyText="No page views in this period"
          />
        </Card>

        <Card padding="md" className="gap-4">
          <CardHeader title="Conversion Funnel" />
          {statsLoading ? (
            <div className="flex flex-col gap-4">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12 w-full rounded-control" />
              ))}
            </div>
          ) : (
            <>
              <ul className="flex flex-col gap-4">
                {funnelData.map((item) => (
                  <li key={item.label}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="text-fg-muted">{item.label}</span>
                      <span className="font-semibold text-fg">{formatNumber(item.value)}</span>
                    </div>
                    <div className="h-3 w-full overflow-hidden rounded-full bg-surface-subtle">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${(item.value / funnelMax) * 100}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>

              <dl className="grid grid-cols-3 gap-4 border-t border-line pt-4 text-center">
                <div>
                  <dt className="text-xs text-fg-muted">Visitor Conversion</dt>
                  <dd className="text-lg font-semibold text-fg">{formatPercent(rates?.visitor)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-fg-muted">Prayer Conversion</dt>
                  <dd className="text-lg font-semibold text-fg">{formatPercent(rates?.prayer)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-fg-muted">Donation Conversion</dt>
                  <dd className="text-lg font-semibold text-fg">{formatPercent(rates?.donation)}</dd>
                </div>
              </dl>
            </>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card padding="md" className="gap-4">
          <CardHeader title="Link Performance" description="Top links by clicks" />
          {loading ? (
            <ListSkeleton />
          ) : linkPerformance.length === 0 ? (
            <EmptyState variant="inline" title="No link clicks recorded yet" />
          ) : (
            <ol className="divide-y divide-line">
              {linkPerformance.slice(0, TOP_LIST_SIZE).map((link, index) => (
                <li key={link.linkId} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-fg">{link.title}</p>
                    <p className="truncate text-xs text-fg-muted">{link.url}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-semibold text-fg">{formatNumber(link.clicks)}</p>
                    <p className="text-xs text-fg-muted">{formatPercent(link.ctr)} CTR</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>

        <Card padding="md" className="gap-4">
          <CardHeader title="Traffic Sources" description="Top referrers" />
          {loading ? (
            <ListSkeleton />
          ) : referrers.length === 0 ? (
            <EmptyState variant="inline" title="No referrer data available" />
          ) : (
            <ol className="divide-y divide-line">
              {referrers.slice(0, TOP_LIST_SIZE).map((ref, index) => (
                <li key={ref.referrer || "direct"} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-subtle text-xs font-semibold text-fg-muted">
                    {index + 1}
                  </span>
                  <p className="min-w-0 flex-1 truncate text-sm text-fg">{ref.referrer || "Direct"}</p>
                  <div className="flex shrink-0 items-baseline gap-2">
                    <span className="font-semibold text-fg">{formatNumber(ref.count)}</span>
                    <span className="text-xs text-fg-muted">{formatPercent(ref.percentage)}</span>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </PageContainer>
  );
};

export default Analytics;
